import React, { forwardRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Paper, Typography } from "@mui/material";
import { getArticleById } from "../../../../services/contentServices";
import ArticleContentPreviewRenderer from "./ArticleContentPreviewRenderer";
import ArticleLinkedPreviewCard from "./ArticleLinkedPreviewCard";
import { formatArticleDate, formatReadingTime } from "./articleMagazineFormatters";
import {
  magazineArticlesGridSx,
  magazineArticlesSectionSx,
  magazineArticleHeaderBlockSx,
  magazineArticleSubtitleSx,
  magazineArticleTitleSx,
  magazineCategoryBadgeSx,
  magazineLinkedArticlesSectionTitleSx,
  magazineMetaPillSx,
  magazinePageContentSx,
  magazineSuggestedProductItemSx,
  magazineSuggestedProductLinkSx,
  magazineSuggestedProductsLinksSx,
  magazineSuggestedProductsSectionSx,
  magazineSuggestedProductsTitleSx,
} from "./articleMagazinePreviewStyles";
import { toRem } from "./articleMagazinePreviewTokens";

const resolveLinkedArticles = async (linkedIds, onError) => {
  if (!linkedIds?.length) {
    return [];
  }
  const summaries = await Promise.all(
    linkedIds.map(async (id) => {
      try {
        const linked = await getArticleById(id);
        return {
          id: linked.id,
          title: linked.title,
          subtitle: linked.subtitle || "",
          image: linked.image || "",
          category: linked.category || "",
          readingMinutes: linked.readingMinutes,
          publishedAt: linked.publishedAt,
        };
      } catch (error) {
        onError?.(error.message);
        return null;
      }
    })
  );
  return summaries.filter(Boolean);
};

const ArticlePublicPreview = forwardRef(({ article, onError }, ref) => {
  const [linkedArticles, setLinkedArticles] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadLinkedArticles = async () => {
      if (!article.linkedArticles?.length) {
        setLinkedArticles([]);
        return;
      }
      const resolved = await resolveLinkedArticles(article.linkedArticles, onError);
      if (!cancelled) {
        setLinkedArticles(resolved);
      }
    };

    loadLinkedArticles();

    return () => {
      cancelled = true;
    };
  }, [article.linkedArticles, onError]);

  return (
    <Paper
      ref={ref}
      elevation={0}
      sx={{
        mt: 1,
        border: "1px solid #e2e8f0",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "#ffffff",
      }}
    >
      <Box sx={{ px: 2, py: 1.25, borderBottom: "1px solid #e8edf3", bgcolor: "#f8fafc" }}>
        <Typography sx={{ fontFamily: "Rubik, sans-serif", fontSize: "0.875rem", color: "#64748b" }}>
          Anteprima pagina articolo (come in FE Application)
        </Typography>
        {!article.isVisible ? (
          <Typography sx={{ fontFamily: "Rubik, sans-serif", fontSize: "0.8125rem", color: "#b30047", mt: 0.5 }}>
            L'articolo non sarà pubblico finchè non verrà salvato e impostato come "Articolo visibile"
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ ...magazinePageContentSx, bgcolor: "#ffffff" }}>
        <Box sx={magazineArticleHeaderBlockSx}>
          <Typography component="h1" sx={magazineArticleTitleSx}>
            {article.title?.trim() || "Titolo articolo"}
          </Typography>

          {article.subtitle?.trim() ? (
            <Typography sx={magazineArticleSubtitleSx}>
              {article.subtitle}
            </Typography>
          ) : null}

          {article.category ? (
            <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
              <Box sx={magazineCategoryBadgeSx}>{article.category.toUpperCase()}</Box>
            </Box>
          ) : null}
        </Box>

        {article.image ? (
          <Box
            component="img"
            src={article.image}
            alt=""
            sx={{
              width: "100%",
              mt: 3,
              borderRadius: toRem(20),
              display: "block",
              maxHeight: toRem(333),
              objectFit: "cover",
            }}
          />
        ) : null}

        {(formatArticleDate(article.publishedAt) || formatReadingTime(article.readingMinutes)) ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Box sx={magazineMetaPillSx}>
              <Typography component="span">{formatArticleDate(article.publishedAt)}</Typography>
              {formatReadingTime(article.readingMinutes) ? (
                <>
                  <Typography component="span" aria-hidden="true">
                    |
                  </Typography>
                  <Typography component="span">{formatReadingTime(article.readingMinutes)}</Typography>
                </>
              ) : null}
            </Box>
          </Box>
        ) : null}

        <Box sx={{ mt: 4, width: "100%" }}>
          <ArticleContentPreviewRenderer content={article.content} />
        </Box>

        {article.suggestedProducts?.length ? (
          <Box sx={magazineSuggestedProductsSectionSx}>
            <Typography sx={magazineSuggestedProductsTitleSx}>
              Prodotti che ti potrebbero interessare:
            </Typography>
            <Box component="div" sx={magazineSuggestedProductsLinksSx}>
              {article.suggestedProducts.map((label, index) => (
                <Box key={label} sx={magazineSuggestedProductItemSx}>
                  {index > 0 ? (
                    <Typography component="span" sx={{ color: "#94a3b8", mx: 0.75 }} aria-hidden="true">
                      |
                    </Typography>
                  ) : null}
                  <Typography component="span" sx={magazineSuggestedProductLinkSx}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}

        {linkedArticles.length ? (
          <Box sx={{ mt: 2 }}>
            <Typography sx={magazineLinkedArticlesSectionTitleSx}>
              Altri articoli che potrebbero interessarti
            </Typography>
            <Box sx={magazineArticlesSectionSx}>
              <Box sx={{ ...magazineArticlesGridSx, mt: 0 }}>
                {linkedArticles.map((linkedArticle) => (
                  <ArticleLinkedPreviewCard key={linkedArticle.id} article={linkedArticle} />
                ))}
              </Box>
            </Box>
          </Box>
        ) : null}
      </Box>
    </Paper>
  );
});

ArticlePublicPreview.displayName = "ArticlePublicPreview";

ArticlePublicPreview.propTypes = {
  article: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    image: PropTypes.string,
    category: PropTypes.string,
    readingMinutes: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    publishedAt: PropTypes.string,
    isVisible: PropTypes.bool,
    linkedArticles: PropTypes.arrayOf(PropTypes.string),
    suggestedProducts: PropTypes.arrayOf(PropTypes.string),
    content: PropTypes.shape({
      blocks: PropTypes.arrayOf(PropTypes.object),
    }),
  }).isRequired,
  onError: PropTypes.func,
};

export default ArticlePublicPreview;
